import { useTranslation, translate } from '@rhino-project/core/i18n';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { sortBy } from 'lodash';

import Player, { PlayerHeader } from './Player';
import ExternalTeams from '../externalTeams/ExternalTeams';
import { mergeObservation } from './mergeObservation';
import { freshPlayerArray, rosterForForm, sorters } from './roster';
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertTitle,
  Button,
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerDialog,
  DrawerHeader,
  DrawerHeading
} from '@heroui/react';
import { Icon } from '@iconify/react';
import {
  useFieldSetErrors,
  useModelActionUpdate,
  useModelInvalidateIndex,
  useModelPathCollection
} from '@rhino-project/core/hooks';
import { ModelFieldGroup } from '@rhino-project/ui-heroui';
import { ModelEditSimple } from '@rhino-project/ui-heroui';
import { useModelEditContext } from '@rhino-project/core/hooks';
import { useController, useFieldArray, useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { networkApiCall } from '@rhino-project/core/lib';

type RhinoRecord = Record<string, unknown>;

const getTeamSeason = (team: RhinoRecord) =>
  (team?.external_linked_team as RhinoRecord)?.external_team_season || 0;

interface RhinoApiError {
  errors: Record<string, string[]> | string[];
}

// A conversion failure is reported against the player it happened on, using a
// path into the report — nothing on a player row renders an error, so on its
// own the failure is invisible. Name the player instead so the scout knows
// which row to go back to.
const PLAYER_ERROR_PATH = /^report\.teams\.(\d+)\.players\.(\d+)\.(.+)$/;

const attributeLabels = (): Record<string, string> => ({
  // The season chip on a pinned row reads "+ Recruit", not "Team season".
  team_season: translate('frontend.messages.recruit_season')
});

// Matches how Rails humanizes an attribute for an error message, which is what
// lets the path it prepends be recognized and taken back off below.
const humanize = (value: string) => {
  const words = value.replace(/[._]/g, ' ');

  return words.charAt(0).toUpperCase() + words.slice(1);
};

const describePlayer = (player: RhinoRecord | undefined, index: number) => {
  const number = (player?.number as string)?.trim();
  const name = [player?.first_name, player?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const identity = [number && `#${number}`, name].filter(Boolean).join(' ');

  return (
    identity || translate('frontend.messages.row_number', { number: index + 1 })
  );
};

const describeSubmitError = (
  path: string,
  message: string,
  teams: RhinoRecord[] | undefined
) => {
  const player = PLAYER_ERROR_PATH.exec(path);
  if (!player) return message;

  const [, teamIndex, playerIndex, attribute] = player;
  const players = teams?.[Number(teamIndex)]?.players as
    RhinoRecord[] | undefined;
  const row = `${teamIndex === '0' ? translate('frontend.messages.home') : translate('frontend.messages.visitor')} ${describePlayer(
    players?.[Number(playerIndex)],
    Number(playerIndex)
  )}`;

  // The message arrives with the whole path humanized onto the front of it
  // ("Report teams 0 players 0 team season must be set..."). Swap that for the
  // name the field goes by on the page.
  const serverPrefix = humanize(path);
  if (!message.startsWith(serverPrefix)) return `${row}: ${message}`;

  const label = attributeLabels()[attribute] ?? humanize(attribute);

  return `${row}: ${label} ${message.slice(serverPrefix.length).trim()}`;
};

const SubmitErrors = ({ messages }: { messages: string[] }) => {
  const { t } = useTranslation();
  return (
    <Alert status="danger" className="mt-4">
      <AlertContent>
        <AlertTitle>
          {t('frontend.messages.the_observation_was_not_submitted')}
        </AlertTitle>
        <AlertDescription>
          <ul>
            {messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </AlertDescription>
      </AlertContent>
    </Alert>
  );
};

interface ScoutEditActionsProps {
  onSubmitErrors: (messages: string[]) => void;
}

// Autosave runs off the form's watch subscription, which only marks a change
// `type` for an edit made through a field. Adding, replacing or clearing rows
// of a field array arrives with no type, and so does the reset react-hook-form
// performs whenever the fetched values change, which is why the subscription
// ignores both rather than saving the form back on every refetch. Neither the
// type nor the name it does carry separates the two, so every roster-shaped
// edit (loading a team's roster, adding blank rows, sorting, clearing a team)
// has to save itself or it is lost when the observation is switched.
const useSaveReport = () => {
  const {
    debouncedMutate: saveObservation,
    methods: { getValues }
  } = useModelEditContext();

  return useCallback(
    () => saveObservation(getValues()),
    [getValues, saveObservation]
  );
};

export const ScoutEditActions = ({ onSubmitErrors }: ScoutEditActionsProps) => {
  const { t } = useTranslation();

  const {
    model,
    modelId,
    debouncedMutate: saveObservation,
    methods: { clearErrors, setError, watch }
  } = useModelEditContext();
  const teamsWatch = useWatch({ name: 'report.teams' }) as
    RhinoRecord[] | undefined;
  const fieldSetError = useFieldSetErrors();
  const onError = useCallback(
    (error: unknown) => {
      clearErrors();

      const errors = (error as Partial<RhinoApiError>)?.errors;

      // A server error or a dropped connection carries no errors at all, and a
      // base error — the guard against completing twice, for one — carries an
      // array rather than anything field shaped.
      if (!errors) {
        onSubmitErrors([
          t(
            'frontend.messages.the_observation_could_not_be_submitted_please_try_again'
          )
        ]);
        return;
      }

      if (Array.isArray(errors)) {
        onSubmitErrors(errors);
        return;
      }

      if (errors.reported_at) {
        setError('observed_at', {
          type: 'manual',
          message: errors.reported_at[0]
        });
      }
      fieldSetError({ errors });
      onSubmitErrors(
        Object.entries(errors).map(([path, [message]]) =>
          describeSubmitError(path, message, teamsWatch)
        )
      );
    },
    [clearErrors, fieldSetError, onSubmitErrors, setError, t, teamsWatch]
  );

  const { mutate: observationComplete, isPending } = useModelActionUpdate(
    model,
    'complete',
    { onError }
  );

  const pinnedCount = teamsWatch?.reduce(
    (count: number, t: RhinoRecord) =>
      count +
      ((t?.players as RhinoRecord[])?.filter((p) => p.convert)?.length || 0),
    0
  );

  const ratedCount = teamsWatch?.reduce(
    (count: number, t: RhinoRecord) =>
      count +
      ((t?.players as RhinoRecord[])?.filter(
        (p) => parseFloat(p.rating as string) > 0
      )?.length || 0),
    0
  );

  useEffect(() => {
    const subscription = watch(
      (observation: unknown, { type }: { type?: string }) => {
        if (!type) return;
        saveObservation(observation);
      }
    );
    return () => subscription.unsubscribe();
  }, [saveObservation, watch]);

  const handlePinnedSubmitClick = () => {
    onSubmitErrors([]);
    observationComplete({ id: modelId, convert: 'pinned' });
  };

  const handleSubmitClick = () => {
    onSubmitErrors([]);
    observationComplete({ id: modelId, convert: 'all' });
  };

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
    setPortalTarget(document.getElementById('scout-edit-actions'));
  }, []);

  if (!portalTarget) return null;

  return createPortal(
    <>
      <Button
        variant="primary"
        isPending={isPending}
        isDisabled={!pinnedCount}
        onPress={handlePinnedSubmitClick}
      >
        <Icon icon="bi:save" />{' '}
        {t('frontend.messages.submit_pinned', { count: pinnedCount })}
      </Button>

      <Button
        variant="primary"
        isPending={isPending}
        isDisabled={!ratedCount}
        onPress={handleSubmitClick}
      >
        <Icon icon="bi:save" />{' '}
        {t('frontend.messages.submit_rated', { count: ratedCount })}
      </Button>
    </>,
    portalTarget
  );
};

interface PlayerAppendProps {
  append: (value: ReturnType<typeof freshPlayerArray>) => void;
}

const PlayerAppend = ({ append }: PlayerAppendProps) => {
  const { t } = useTranslation();

  const saveReport = useSaveReport();

  const handlePress = () => {
    append(freshPlayerArray());
    saveReport();
  };

  return (
    <Button className="mt-2" onPress={handlePress}>
      {t('frontend.messages.add_players')}
    </Button>
  );
};

interface PlayerField {
  id: string;
  [key: string]: unknown;
}

interface PlayerListProps {
  teamIndex: number;
  players: PlayerField[];
  append: (value: ReturnType<typeof freshPlayerArray>) => void;
  replace: (value: unknown[]) => void;
  getValues: (name: string) => unknown;
}

export const PlayerList = ({
  teamIndex,
  players,
  append,
  replace,
  getValues
}: PlayerListProps) => {
  const saveReport = useSaveReport();

  const handleHeaderClick = (header: string) => {
    // Sort the real form values (which retain the external_player id) instead
    // of useFieldArray's `fields`, whose `id` is replaced by an RHF-internal uuid.
    const current =
      (getValues(`report.teams.${teamIndex}.players`) as RhinoRecord[]) ?? [];
    replace(sortBy(current, [sorters[header]]));
    saveReport();
  };

  return (
    <>
      <PlayerHeader onClick={handleHeaderClick} />
      {players.map((player, index) => (
        <Player key={player.id} index={index} teamIndex={teamIndex} />
      ))}
      {players?.length > 0 && <PlayerAppend append={append} />}
    </>
  );
};

interface TeamProps {
  teamIndex: number;
}

export const Team = ({ teamIndex }: TeamProps) => {
  const { t } = useTranslation();

  const teamKey = `report.teams.${teamIndex}`;
  const playersKey = `${teamKey}.players`;
  const endpoint = useModelPathCollection('external_player');
  const queryClient = useQueryClient();
  const {
    methods: { getValues }
  } = useModelEditContext();
  const saveReport = useSaveReport();

  const {
    field: { onChange: onChangeExternalLinkedTeam }
  } = useController({
    name: `${teamKey}.external_linked_team`
  });
  const {
    field: { onChange: onChangeExternalTeam }
  } = useController({
    name: `${teamKey}.external_team`
  });
  const {
    field: { onChange: onChangeName }
  } = useController({ name: `${teamKey}.name` });
  const {
    field: { onChange: onChangeSeason }
  } = useController({
    name: `${teamKey}.season`
  });
  const {
    fields: players,
    append,
    remove,
    replace
  } = useFieldArray({
    name: playersKey
  });
  // Watched rather than read: the team search resolves the season from this
  // date, and the date is edited from a field outside this component.
  const observedAt = useWatch({ name: 'observed_at' }) as string | undefined;

  const handleTeamChange = (newTeam: RhinoRecord) => {
    const teamSeason = getTeamSeason(newTeam);

    onChangeExternalLinkedTeam(newTeam.external_linked_team);
    onChangeExternalTeam(newTeam.external_team);
    onChangeName(newTeam.name);
    onChangeSeason(newTeam.season);

    if (teamSeason) {
      queryClient
        .query({
          queryKey: ['external_player', endpoint, newTeam],
          queryFn: async () => {
            const response = await networkApiCall(endpoint, {
              params: {
                filter: {
                  external_rosters: {
                    external_team_season: teamSeason
                  }
                },
                limit: 100,
                order: 'last_name'
              }
            });

            return response ?? null;
          }
        })
        .then((data) => {
          const result = data as { data?: { results?: unknown[] } };
          replace(rosterForForm(result?.data?.results));
          saveReport();
        })
        .catch(() => {
          // Leaving the last team's roster in place under the team just picked
          // is the worst of the options: those rows read as this team's players
          // and could be pinned as them.
          replace(freshPlayerArray());
          saveReport();
        });
    } else if ((newTeam?.external_linked_team as RhinoRecord)?.team_name) {
      replace(freshPlayerArray());
      saveReport();
    } else {
      remove();
      saveReport();
    }
  };

  return (
    <div>
      <ExternalTeams
        name={`team-${teamIndex}`}
        placeholder={
          teamIndex === 0
            ? t('frontend.messages.home_team')
            : t('frontend.messages.visitor_team')
        }
        teamValue={getValues(teamKey)}
        referenceDate={observedAt}
        onTeamSelect={handleTeamChange}
      />
      <PlayerList
        teamIndex={teamIndex}
        players={players}
        append={append}
        replace={replace}
        getValues={getValues}
      />
    </div>
  );
};

const Teams = () => {
  const { fields: teams } = useFieldArray({
    name: 'report.teams'
  });

  return (
    <div className="grid grid-cols-2 gap-4">
      {teams.map((t, teamIndex) => (
        <Team key={t.id} teamIndex={teamIndex} />
      ))}
    </div>
  );
};

// The game note is edited in a side drawer (the reactstrap Offcanvas before
// the HeroUI v3 migration) so the header row stays compact; the field still
// lives in the observation edit form, so the autosave watch picks it up.
const GameNote = () => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button variant="ghost" onPress={() => setIsOpen(true)}>
        <Icon icon="bi:pencil-square" /> {t('frontend.messages.game_note')}
      </Button>
      <DrawerBackdrop isOpen={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent placement="right">
          <DrawerDialog className="h-full w-96 max-w-[90vw]">
            <DrawerCloseTrigger />
            <DrawerHeader>
              <DrawerHeading>{t('frontend.messages.game_note')}</DrawerHeading>
            </DrawerHeader>
            <DrawerBody>
              <ModelFieldGroup
                path="game_note"
                label={t('frontend.messages.note')}
              />
            </DrawerBody>
          </DrawerDialog>
        </DrawerContent>
      </DrawerBackdrop>
    </>
  );
};

interface ScoutProps {
  observation: RhinoRecord;
}

const Scout = ({ observation }: ScoutProps) => {
  const { t } = useTranslation();

  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const { invalidateIndex: invalidateObservations } =
    useModelInvalidateIndex('observation');

  return (
    <ModelEditSimple
      model="observation"
      modelId={observation?.id as string | number}
      fallback={false}
      debounceDelay={1000}
      disabled={false}
      // The page autosaves and stays open for a whole game, so refetching the
      // observation after each save would reset the form while the scout is
      // still entering players, remounting the rows and taking the focus with
      // it. Nothing else on the page reads the observation, so only the list
      // the picker draws from is refreshed.
      mutationOptions={{ onSuccess: () => invalidateObservations() }}
      queryOptions={
        {
          structuralSharing: mergeObservation
        } as Parameters<typeof ModelEditSimple>[0]['queryOptions']
      }
    >
      <ScoutEditActions onSubmitErrors={setSubmitErrors} />
      {submitErrors.length > 0 && <SubmitErrors messages={submitErrors} />}
      <div className="grid grid-cols-12 gap-4 mt-4">
        <div className="col-span-2">
          <ModelFieldGroup
            label={t('frontend.messages.date')}
            path="observed_at"
          />
        </div>
        <div className="col-span-4">
          <ModelFieldGroup path="observing" />
        </div>
        <div className="col-span-4">
          <ModelFieldGroup path="venue" />
        </div>
        <div className="col-span-2 flex items-end">
          <GameNote />
        </div>
      </div>
      <div className="mt-4">
        <Teams />
      </div>
    </ModelEditSimple>
  );
};

export default Scout;
